import {
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import type { FieldArrayWithId } from "react-hook-form"
import { useFormContext, useWatch } from "react-hook-form"

import {
  InventoryLotTableHeaderCells,
  InventoryLotTableRowCells,
  InventoryPalletTableHeaderCells,
  InventoryPalletTableRowCells,
} from "../../../../components"
import { LOT_COLUMN_COUNT, PALLET_COLUMN_COUNT_WITHOUT_QOH } from "../../../../constants"
import type { InterTransferPalletSelectionFormValues } from "../../schemas/transfer"
import { getLotRowBorderClassName } from "../../utils/transfer"

import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import type { WarehouseArea } from "@/graphql/codegen/graphql"
import { formatNumber, roundToPrecision } from "@/utils/number"
import { formatWarehouseLabel } from "@/utils/warehouse"

const QTY_COLUMN_COUNT = 2
const DESTINATION_COLUMN_COUNT = 2
const DELETE_COLUMN_COUNT = 1
const COLUMN_COUNT =
  PALLET_COLUMN_COUNT_WITHOUT_QOH + LOT_COLUMN_COUNT + QTY_COLUMN_COUNT + DESTINATION_COLUMN_COUNT + DELETE_COLUMN_COUNT

interface InterTransferPalletQuantitiesProps {
  targetWarehouseArea: WarehouseArea
  palletFields: FieldArrayWithId<InterTransferPalletSelectionFormValues, "pallets">[]
  uomName: string
  onLotRemove: (palletIndex: number, lotIndex: number) => void
}

export const InterTransferPalletQuantities: FC<InterTransferPalletQuantitiesProps> = ({
  targetWarehouseArea,
  palletFields,
  uomName,
  onLotRemove,
}) => {
  const { control } = useFormContext<InterTransferPalletSelectionFormValues>()
  const palletValues = useWatch({ control, name: "pallets" })

  return (
    <div className='flex min-h-0 flex-1 flex-col rounded-lg border border-gray-400 p-4'>
      <Typography variant='h6' className='mb-4 font-bold leading-10 text-gray-800'>
        2. Specify Quantity to Move
      </Typography>
      <TableContainer className='overflow-auto'>
        <Table stickyHeader>
          <TableHead className='bg-gray-100'>
            <TableRow>
              <InventoryPalletTableHeaderCells showQoh={false} />
              <InventoryLotTableHeaderCells quantityColumnLabel='Current Lot UoM' />
              <TableCell className='min-w-[160px] whitespace-nowrap'>
                Qty to Move<span className='text-secondary-600'>*</span>
              </TableCell>
              <TableCell className='min-w-[110px] whitespace-nowrap'>Remaining Qty</TableCell>
              <TableCell className='min-w-[160px] whitespace-nowrap'>Destination</TableCell>
              <TableCell className='min-w-[140px] whitespace-nowrap'>LPN</TableCell>
              <TableCell className='w-12' />
            </TableRow>
          </TableHead>
          <TableBody>
            {!palletFields.length && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className='py-8'>
                  <p className='flex flex-col items-center gap-y-2 text-center'>
                    <span className='font-semibold text-gray-700'>No pallets selected</span>
                    <span className='max-w-[340px]'>
                      Select pallet(s) or lot(s) from the table above using the checkboxes to specify quantities.
                    </span>
                  </p>
                </TableCell>
              </TableRow>
            )}
            {palletFields.map((pallet, palletIndex) =>
              pallet.lots.map((lot, lotIndex) => {
                const isFirstLot = lotIndex === 0
                const isLastLot = lotIndex === pallet.lots.length - 1
                const qtyToMove = palletValues[palletIndex]?.lots[lotIndex]?.qtyToMove
                const remainingQty = Math.max(roundToPrecision((lot.totalUnits ?? 0) - (qtyToMove ?? 0), 2), 0)

                return (
                  <TableRow
                    key={lot.rawInventoryId}
                    className={getLotRowBorderClassName(isLastLot)}
                    data-testid={`transfer.pallet-quantities.row-${lot.rawInventoryId}`}
                  >
                    <InventoryPalletTableRowCells
                      pallet={pallet}
                      isFirstLot={isFirstLot}
                      uomName={uomName}
                      showQoh={false}
                    />
                    <InventoryLotTableRowCells lot={lot} uomName={uomName} showStatusIcon={false} />
                    <TableCell>
                      <FormNumericField
                        control={control}
                        name={`pallets.${palletIndex}.lots.${lotIndex}.qtyToMove`}
                        allowNegative={false}
                        decimalScale={2}
                        slotProps={{
                          input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> },
                        }}
                        className='w-[140px]'
                        data-testid={`transfer.pallet-quantities.qty-to-move-field-${palletIndex}-${lotIndex}`}
                      />
                    </TableCell>
                    <TableCell className='whitespace-nowrap'>{`${formatNumber(remainingQty)} ${uomName}`}</TableCell>
                    {isFirstLot ? (
                      <>
                        <TableCell>{formatWarehouseLabel(targetWarehouseArea)}</TableCell>
                        <TableCell>
                          <FormTextField
                            control={control}
                            name={`pallets.${palletIndex}.lpn`}
                            className='w-[140px]'
                            data-testid={`transfer.pallet-quantities.lpn-field-${palletIndex}`}
                          />
                        </TableCell>
                      </>
                    ) : (
                      <TableCell colSpan={DESTINATION_COLUMN_COUNT} />
                    )}
                    <TableCell>
                      <Tooltip title='Remove lot'>
                        <IconButton
                          size='small'
                          onClick={() => onLotRemove(palletIndex, lotIndex)}
                          data-testid={`transfer.pallet-quantities.delete-button-${palletIndex}-${lotIndex}`}
                        >
                          <DeleteOutlined />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}
